import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  wordpressApi,
  ConnectWordPressInput,
  WordPressChannel,
  WordPressTermResult,
} from '@/services/wordpress.api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';

/**
 * React Query hooks for WordPress channel management.
 *
 * Team-scoped so switching teams (God Mode) triggers an automatic refetch,
 * matching the pattern in `useCanvaImport.ts` / `useGDriveImport.ts`.
 */

export const WORDPRESS_KEYS = {
  channels: (teamId: number | null) => [...teamQueryKeys.channels.all(teamId), 'wordpress'] as const,
  categories: (channelId: number) => ['wordpress-categories', channelId] as const,
  tags: (channelId: number) => ['wordpress-tags', channelId] as const,
};

/**
 * GET /api/wordpress/channels — the team's connected WordPress sites.
 */
export function useWordPressChannels() {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<WordPressChannel[]>({
    queryKey: WORDPRESS_KEYS.channels(teamId),
    queryFn: () => wordpressApi.getChannels(),
    enabled: hasTeam,
    staleTime: 30 * 1000,
  });
}

/**
 * POST /api/wordpress/connect
 */
export function useConnectWordPress() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation<WordPressChannel, unknown, ConnectWordPressInput>({
    mutationFn: (input) => wordpressApi.connect(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    },
  });
}

/**
 * POST /api/wordpress/channels/:id/reprobe
 */
export function useReprobeWordPressChannel() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (channelId: number) => wordpressApi.reprobe(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    },
  });
}

/**
 * DELETE /api/wordpress/channels/:id
 */
export function useDisconnectWordPressChannel() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (channelId: number) => wordpressApi.disconnect(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    },
  });
}

/**
 * GET /api/wordpress/channels/:id/categories
 * Lazy-loaded only once a target site is chosen on the preview/confirm
 * screen (decision F).
 */
export function useWordPressCategories(channelId: number | undefined) {
  return useQuery({
    queryKey: WORDPRESS_KEYS.categories(channelId ?? 0),
    queryFn: () => wordpressApi.getCategories(channelId as number),
    enabled: !!channelId,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

/**
 * GET /api/wordpress/channels/:id/tags
 */
export function useWordPressTags(channelId: number | undefined) {
  return useQuery({
    queryKey: WORDPRESS_KEYS.tags(channelId ?? 0),
    queryFn: () => wordpressApi.getTags(channelId as number),
    enabled: !!channelId,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

/**
 * POST /api/wordpress/channels/:id/categories
 * Creates a new category "on the fly" from the preview/confirm screen's
 * category picker (decision F). Invalidates the categories query for this
 * channel so the new term appears in the picker immediately.
 */
export function useCreateWordPressCategory(channelId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation<WordPressTermResult, unknown, string>({
    mutationFn: (name) => wordpressApi.createCategory(channelId as number, name),
    onSuccess: () => {
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: WORDPRESS_KEYS.categories(channelId) });
      }
    },
  });
}

/**
 * POST /api/wordpress/channels/:id/tags
 * Creates a new tag "on the fly" from the preview/confirm screen's tag
 * picker (decision F). Invalidates the tags query for this channel so the
 * new term appears in the picker immediately.
 */
export function useCreateWordPressTag(channelId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation<WordPressTermResult, unknown, string>({
    mutationFn: (name) => wordpressApi.createTag(channelId as number, name),
    onSuccess: () => {
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: WORDPRESS_KEYS.tags(channelId) });
      }
    },
  });
}
